/* eslint-disable no-unused-expressions */
import React, { useState } from 'react';

import { Media } from '../../contexts/media/MediaContext';
import useGrid from '../../hooks/grid/useGrid';
import useFeatureToggle from '../../hooks/use-feature-toggle/useFeatureToggle';
import { NonClickableContentCardGridContent, FeatureToggle } from '../../models/app-data-model';
import ButtonWrapper from '../button-wrapper/button-wrapper';
import CardGridContainerDesktop from '../card-grid-container-desktop/card-grid-container-desktop';
import ContentCard from '../content-card/content-card';
import './non-clickable-content-card-grid.scss';

export type NonClickableContentCardGridProps = {
  content: NonClickableContentCardGridContent;
  featureToggles: FeatureToggle[];
  corewellStyleOverrides?: boolean;
};

const NonClickableContentCardGrid: React.FC<NonClickableContentCardGridProps> = ({
  content,
  featureToggles,
  corewellStyleOverrides
}: NonClickableContentCardGridProps) => {
  const showNoClickGrid: boolean = useFeatureToggle('non-clickable-card-grid', featureToggles);
  const cardGrid = useGrid(content.cards, 4);
  const cardGridMobile = useGrid(content.cards, 2);
  const [isShowAllToggled, toggleShowAll] = useState(false);

  if (!showNoClickGrid) {
    return null;
  }

  const ColumnRender = (mobile: boolean): JSX.Element[] => {
    let displayGrid = cardGrid;

    if (mobile && !isShowAllToggled) {
      displayGrid = cardGridMobile.slice(0, 2);
    } else if (mobile && isShowAllToggled) {
      displayGrid = cardGridMobile;
    }

    const Column = displayGrid?.map((cardRow, rowIndex) => {
      const cardRowClass = `card-row ${rowIndex === 0 ? 'first-row' : ''} ${rowIndex + 1 === displayGrid.length ? 'last-row' : ''}`;

      return (
        <div data-testid={`cardrow${rowIndex}`} key={rowIndex} className={cardRowClass}>
          {cardRow.items?.map((card, cardIndex) => (
            <ContentCard
              key={cardIndex}
              cardImage={mobile ? card.content_card.image_square : card.content_card.image_landscape}
              cardWidth={mobile ? 2 : 4}
              includeImagePadding={content?.include_image_padding}
              content={card}
              naturalWidthImage={content?.natural_width_image}
              fadedStyles={mobile && !isShowAllToggled ? rowIndex + 1 === displayGrid.length : false}
              corewellStyleOverrides={corewellStyleOverrides}
            />
          ))}
        </div>
      );
    });

    return Column;
  };

  return (
    <>
      <Media lessThan="desktop">
        <div data-testid="nonClickableCardGridMobile" className={`non-clickable ${content?.card_alignment}`}>
          <CardGridContainerDesktop
            maxColumns={2}
            tertiaryButton={content?.tertiary_button}
            hideTertiaryButton={!isShowAllToggled}
            noBackgroundDisplay={content?.hide_grey_background}
          >
            {ColumnRender(true)}
            {cardGridMobile.length >= 2 && !isShowAllToggled && (
              <span className="grid-bottom-button">
                <ButtonWrapper
                  type="tertiary"
                  label={'Show All'}
                  styleOverride="transparent-bg"
                  isShowMore={true}
                  onClick={() => toggleShowAll(true)}
                ></ButtonWrapper>
              </span>
            )}
          </CardGridContainerDesktop>
        </div>
      </Media>

      <Media greaterThanOrEqual="desktop">
        <div data-testid="nonClickableCardGridDesktop" className={`non-clickable ${content?.card_alignment}`}>
          <CardGridContainerDesktop
            maxColumns={4}
            tertiaryButton={content?.tertiary_button}
            noBackgroundDisplay={content?.hide_grey_background}
          >
            {ColumnRender(false)}
          </CardGridContainerDesktop>
        </div>
      </Media>
    </>
  );
};

export default NonClickableContentCardGrid;
